import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { projects } from '../data/projects';
import { Project } from '../types/project';
import { Helmet } from 'react-helmet-async';
import ArchitectureDiagram from '../components/ArchitectureDiagram';
import AnalyticsDashboard from '../components/AnalyticsDashboard';

// 한글 파일명 URL 인코딩 헬퍼 함수
const encodeImagePath = (path: string): string => {
  // 경로를 디렉토리와 파일명으로 분리
  const parts = path.split('/');
  const filename = parts.pop();
  if (!filename) return path;
  
  // 파일명만 인코딩하고 경로는 그대로 유지
  const encodedFilename = encodeURIComponent(filename);
  return [...parts, encodedFilename].join('/');
};

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showSwipeGuide, setShowSwipeGuide] = useState(true);
  const [showArchitecture, setShowArchitecture] = useState(false);

  const project = projects.find(p => p.id === Number(id)) as Project | undefined;

  // ✅ 연도 기준 내림차순 정렬된 배열 사용
  const sortedProjects = useMemo(() => 
    [...projects]
      .filter((p) => !p.isStudent)
      .sort((a, b) => Number(b.date) - Number(a.date))
  , [projects]);

  const currentIndex = sortedProjects.findIndex(p => p.id === Number(id));
  const prevProject = currentIndex > 0 ? sortedProjects[currentIndex - 1] : null;
  const nextProject = currentIndex < sortedProjects.length - 1 ? sortedProjects[currentIndex + 1] : null;

  // 이미지 배열 생성 (한글 파일명 URL 인코딩 처리)
  const allImages = useMemo(() => {
    if (!project) return [];
    
    // ID 16번 프로젝트는 메인 이미지만 보여줌
    if (project.id === 16) {
      return [encodeImagePath(project.image)];
    }

    if (project.additionalImages) {
      // 디자인 프로젝트인 경우 (id가 1-11인 경우) 첫 번째 이미지를 첫 번째로, 메인 이미지를 두 번째로 배치
      if (project.id >= 1 && project.id <= 11) {
        const [first, ...rest] = project.additionalImages;
        const images = [];
        if (first) images.push(encodeImagePath(first));
        images.push(encodeImagePath(project.image));
        if (rest.length > 0) images.push(...rest.map(encodeImagePath));
        return images;
      } else {
        // 다른 프로젝트들은 메인 이미지를 첫 번째로, 나머지는 순서대로
        return [encodeImagePath(project.image), ...project.additionalImages.map(encodeImagePath)];
      }
    }
    return [encodeImagePath(project.image)];
  }, [project]);

  // 스와이프 핸들러
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goToNext(),
    onSwipedRight: () => goToPrevious(),
    trackMouse: true
  });

  // 스와이프 가이드 메시지 3초 후 숨기기
  useEffect(() => {
    if (isOpen && showSwipeGuide) {
      const timer = setTimeout(() => {
        setShowSwipeGuide(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, showSwipeGuide]);

  const openLightbox = useCallback((index: number) => {
    setCurrentImageIndex(index);
    setIsOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    if (project && project.additionalImages) {
      setCurrentImageIndex(prev => (prev - 1 + allImages.length) % allImages.length);
    }
  }, [project, allImages]);

  const goToNext = useCallback(() => {
    if (project && project.additionalImages) {
      setCurrentImageIndex(prev => (prev + 1) % allImages.length);
    }
  }, [project, allImages]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  }, [closeLightbox, goToPrevious, goToNext]);

  React.useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return <div>Project not found</div>;
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <Helmet>
        <title>{project.title} | Sangjin Lee</title>
        <meta name="description" content={project.description} />
        <meta property="og:title" content={`${project.title} | Sangjin Lee`} />
        <meta property="og:description" content={project.description} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://sjlee.co.kr${project.image}`} />
        <meta property="og:url" content={`https://sjlee.co.kr/portfolio/${id}`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 py-20">
          {/* Back Button */}
          <button
            onClick={() => navigate('/portfolio')}
            className="mb-10 text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-100 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Projects
          </button>

          {/* Title & Meta */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
            <h1 className="text-4xl font-light tracking-tight text-gray-800 dark:text-gray-200 mb-4">{project.title}</h1>
            <div className="flex items-center gap-4 text-gray-600 dark:text-gray-200">
              <span>{project.date}</span>
              <span>•</span>
              <span>{project.location}</span>
            </div>
          </motion.div>

          {/* Description */}
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed mb-12"
          >
            {project.description}
          </motion.p>

          {/* Image Gallery */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-10 mb-12">
            {allImages.map((image, index) => (
              <div
                key={index}
                className="relative cursor-pointer group"
                onClick={() => openLightbox(index)}
              >
                <LazyLoadImage
                  src={image}
                  alt={`${project.title} - ${index + 1}`}
                  effect="blur"
                  className="w-full h-auto rounded-lg shadow-lg object-contain group-hover:opacity-90 transition-opacity duration-300"
                  placeholderSrc={image}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
                    Click to enlarge
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Tags */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-sm text-gray-600 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* External Links */}
          {(project.githubUrl || project.liveUrl) && (
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
              <div className="flex gap-4">
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded hover:bg-gray-700 dark:hover:bg-gray-600 transition"
                  >
                    <i className="fab fa-github"></i>
                    View on GitHub
                  </a>
                )}
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition"
                  >
                    <i className="fas fa-external-link-alt"></i>
                    Live Demo
                  </a>
                )}
              </div>
            </motion.div>
          )}

          {/* Key Metrics */}
          {project.metrics && project.metrics.length > 0 && (
            <motion.section initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
              <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200 mb-6">Key Metrics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {project.metrics.map((metric, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                    <div className="text-sm text-gray-500 dark:text-gray-200 mb-1">{metric.label}</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{metric.value}</div>
                    {metric.improvement && (
                      <div className="text-xs text-green-600 dark:text-green-400 mt-1">{metric.improvement}</div>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Technical Challenges */}
          {project.challenges && project.challenges.length > 0 && (
            <motion.section initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
              <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200 mb-6">Technical Challenges</h2>
              <div className="space-y-6">
                {project.challenges.map((challenge, idx) => (
                  <div key={idx} className="border-l-4 border-gray-800 dark:border-gray-800 pl-4 py-2">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                      Problem: {challenge.problem}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-200 mb-2">
                      <strong>Solution:</strong> {challenge.solution}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-200">
                      <strong>Impact:</strong> {challenge.impact}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* System Architecture - Collapsible */}
          {project.id === 17 && (
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
              <button
                onClick={() => setShowArchitecture(!showArchitecture)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition mb-4"
              >
                <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200">System Architecture</h2>
                <svg
                  className={`w-5 h-5 text-gray-600 dark:text-gray-200 transition-transform ${showArchitecture ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showArchitecture && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ArchitectureDiagram />
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Analytics Dashboard */}
          {project.hasAnalyticsDashboard && (
            <motion.section initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
              <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200 mb-6">Analytics Dashboard</h2>
              <AnalyticsDashboard />
            </motion.section>
          )}

          {/* Long-form Description */}
          {project.descriptionText && (
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-16">
              <h2 className="text-2xl font-light text-gray-800 dark:text-gray-200 mb-6">Details</h2>
              <div
                className="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-100 prose-hr:border-gray-800 dark:prose-hr:border-gray-800 prose-p:text-gray-700 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 details-content"
                dangerouslySetInnerHTML={{ __html: project.descriptionText }}
              />
            </motion.div>
          )}

          {/* Navigation */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="flex justify-between items-center mt-16 pt-10 border-t border-gray-200 dark:border-gray-800"
          >
            {prevProject ? (
              <button
                onClick={() =>
                  navigate(
                    prevProject.isComputer
                      ? `/portfolio/code/${prevProject.id}`
                      : `/portfolio/arch-pro/${prevProject.id}`
                  )
                }
                className="flex items-center text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-100"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
            ) : <div />}
            {nextProject ? (
              <button
                onClick={() =>
                  navigate(
                    nextProject.isComputer
                      ? `/portfolio/code/${nextProject.id}`
                      : `/portfolio/arch-pro/${nextProject.id}`
                  )
                }
                className="flex items-center text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Next
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : <div />}
          </motion.div>

          {/* Image Modal */}
          {isOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
              onClick={closeLightbox}
            >
              <div 
                className="relative max-w-6xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
                {...swipeHandlers}
              >
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 hidden md:block"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 hidden md:block"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="relative">
                  <LazyLoadImage
                    src={allImages[currentImageIndex]}
                    alt={`${project.title} - ${currentImageIndex + 1}`}
                    className="w-full h-auto max-h-[90vh] object-contain"
                    effect="blur"
                    placeholderSrc={allImages[currentImageIndex]}
                  />
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                    {currentImageIndex + 1} / {allImages.length}
                  </div>
                  {/* 모바일에서만 표시되는 스와이프 가이드 */}
                  {showSwipeGuide && (
                    <div className="md:hidden absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg animate-fade-out">
                        Swipe left/right to navigate
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDetail;
